import { useState, useEffect } from 'react';
import { useLanguage } from '../../../utils/i18n';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Stack
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';

const EVENT_KEY_I18N_MAP = {
  over_speed: 'driverScore.eventOverSpeed',
  idle_time: 'driverScore.eventIdleTime',
  harsh_braking: 'driverScore.eventHarshBraking',
  harsh_acceleration: 'driverScore.eventHarshAcceleration',
  yawning: 'driverScore.eventYawning',
  smoking: 'driverScore.eventSmoking',
  sleepy: 'driverScore.eventSleepy',
  distraction: 'driverScore.eventDistraction',
  phone_call: 'driverScore.eventPhoneCall',
  covering_camera: 'driverScore.eventCoveringCamera'
};

const formatEventKey = (key, t) => {
  if (!key) return '';
  const fallback = key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
  const i18nKey = EVENT_KEY_I18N_MAP[key];
  if (i18nKey && typeof t === 'function') {
    return t(i18nKey, fallback);
  }
  return fallback;
};

export default function EvidenceDialog({ open, onClose, eventKey, source, groupedEvidence = {} }) {
  const { t } = useLanguage(); // EvidenceDialog
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    if (open) {
      const dateKeys = Object.keys(groupedEvidence || {});
      if (dateKeys.length === 1) {
        setSelectedDate(dateKeys[0]);
      } else {
        setSelectedDate(null);
      }
    }
  }, [open, eventKey, source, groupedEvidence]);

  const dates = Object.keys(groupedEvidence || {});
  const currentItems = selectedDate ? groupedEvidence[selectedDate] || [] : [];
  const sourceLabel = source === 'VSS' ? 'VSS Camera' : 'GPS Telematik';
  const sourceChipBg = source === 'VSS' ? '#f3e5f5' : '#e3f2fd';
  const sourceChipColor = source === 'VSS' ? '#6a1b9a' : '#1565c0';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ m: 0, p: 2, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {selectedDate && dates.length > 1 && (
              <IconButton size="small" onClick={() => setSelectedDate(null)} sx={{ mr: 0.5 }}>
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            )}
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6" fontWeight="bold">
                  {formatEventKey(eventKey, t)}
                </Typography>
                <Chip
                  label={sourceLabel}
                  size="small"
                  sx={{
                    bgcolor: sourceChipBg,
                    color: sourceChipColor,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    height: 22
                  }}
                />
              </Stack>
              {selectedDate && (
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {t('driverScore.dateLabel', 'Date')}: {selectedDate} ({currentItems.length} {t('driverScore.eventsSuffix', 'events')})
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 2, minHeight: 250 }}>
        {dates.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <Typography variant="body2" color="text.secondary">
              {t('driverScore.noEvidenceDetail', 'No evidence detail')}
            </Typography>
          </Box>
        ) : selectedDate === null ? (
          /* Step 1: Daftar Tanggal */
          <Box sx={{ maxHeight: 420, overflowY: 'auto' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, px: 0.5 }}>
              {t('driverScore.selectDateToView', 'Select date to view event detail:')}
            </Typography>
            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <List disablePadding>
                {dates.map((dateStr, idx) => {
                  const items = groupedEvidence[dateStr] || [];
                  return (
                    <Box key={dateStr}>
                      {idx > 0 && <Divider />}
                      <ListItem disablePadding>
                        <ListItemButton onClick={() => setSelectedDate(dateStr)} sx={{ py: 1.5, px: 2 }}>
                          <ListItemText
                            primary={
                              <Typography variant="body2" fontWeight="600">
                                {dateStr}
                              </Typography>
                            }
                          />
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip
                              label={`${items.length} ${t('driverScore.eventsSuffix', 'events')}`}
                              size="small"
                              variant="outlined"
                              sx={{ fontWeight: 500, fontSize: '0.75rem' }}
                            />
                            <ChevronRightIcon fontSize="small" color="action" />
                          </Stack>
                        </ListItemButton>
                      </ListItem>
                    </Box>
                  );
                })}
              </List>
            </Paper>
          </Box>
        ) : (
          /* Step 2: Tabel Detail Kejadian per Tanggal */
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420, overflow: 'auto', borderRadius: 2 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>{t('driverScore.startTime', 'Start Time')}</TableCell>
                  {source === 'GPS' ? (
                    <>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>{t('driverScore.endTime', 'End Time')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>{t('driverScore.location', 'Location')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }} align="right">
                        {t('driverScore.speed', 'Speed')}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }} align="right">
                        {t('driverScore.duration', 'Duration')}
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>{t('driverScore.vehicle', 'Vehicle')}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f8fafc' }}>{t('driverScore.violation', 'Violation')}</TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {currentItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={source === 'GPS' ? 5 : 3} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                      {t('driverScore.noDataForDate', 'No data for this date')}
                    </TableCell>
                  </TableRow>
                ) : (
                  currentItems.map((item, idx) => {
                    if (source === 'GPS') {
                      const rawStartTime = item.start_time || item.createtime;
                      const startTimeFormatted = rawStartTime ? dayjs(rawStartTime).format('DD MMM YYYY HH:mm:ss') : '-';

                      const rawEndTime = item.stop_time || item.end_time || item.endTime;
                      const endTimeFormatted = rawEndTime ? dayjs(rawEndTime).format('DD MMM YYYY HH:mm:ss') : '-';

                      const speedFormatted = item.speed != null ? `${item.speed} ${t('driverScore.kmPerHour', 'km/h')}` : '-';
                      const durationFormatted = item.duration || (item.total_duration != null ? `${item.total_duration}s` : '-');

                      return (
                        <TableRow key={idx} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>{startTimeFormatted}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>{endTimeFormatted}</TableCell>
                          <TableCell>{item.location || '-'}</TableCell>
                          <TableCell align="right">{speedFormatted}</TableCell>
                          <TableCell align="right">{durationFormatted}</TableCell>
                        </TableRow>
                      );
                    } else {
                      const rawStartTime = item.event_time || item.start_time || item.reportTime || item.createtime;
                      const startTimeFormatted = rawStartTime ? dayjs(rawStartTime).format('DD MMM YYYY HH:mm:ss') : '-';

                      return (
                        <TableRow key={idx} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>{startTimeFormatted}</TableCell>
                          <TableCell>{item.vehicle || item.plateNo || item.deviceName || '-'}</TableCell>
                          <TableCell>{item.violation || item.alarmTypeValue || '-'}</TableCell>
                        </TableRow>
                      );
                    }
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0' }}>
        <Button onClick={onClose} variant="outlined" size="small" color="inherit">
          {t('general.close', 'Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

EvidenceDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  eventKey: PropTypes.string,
  source: PropTypes.oneOf(['GPS', 'VSS']),
  groupedEvidence: PropTypes.object
};
